import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import { Box } from '@mui/material';
import { useAppTheme } from '../App';
import 'highlight.js/styles/github-dark.css'; // Import syntax highlighting styles

/**
 * MarkdownRenderer Component
 * 
 * Renders markdown content with support for:
 * - Headings (H1-H6)
 * - Paragraphs and line breaks
 * - Bold and italic text
 * - Ordered and unordered lists
 * - Task lists (checkboxes)
 * - Code blocks with syntax highlighting
 * - Inline code
 * - Blockquotes
 * - Tables
 * - Links
 * - Horizontal rules
 * - GitHub Flavored Markdown (GFM)
 * 
 * Automatically adapts styling for light and dark themes.
 */

interface MarkdownRendererProps {
    content: string;
    sx?: Record<string, unknown>; // Allow custom styling
}

const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ content, sx = {} }) => {
    const { isDarkMode } = useAppTheme();

    return (
        <Box
            sx={{
                '& .markdown-content': {
                    color: isDarkMode ? '#ececf1' : '#374151',
                    lineHeight: 1.6,
                    fontSize: '1rem',
                    fontWeight: 400,

                    // Headings
                    '& h1, & h2, & h3, & h4, & h5, & h6': {
                        color: isDarkMode ? '#ececf1' : '#1f2937',
                        fontWeight: 600,
                        marginTop: '1.5em',
                        marginBottom: '0.5em',
                        lineHeight: 1.3,
                    },
                    '& h1': { fontSize: '1.875rem' },
                    '& h2': { fontSize: '1.5rem' },
                    '& h3': { fontSize: '1.25rem' },
                    '& h4': { fontSize: '1.125rem' },
                    '& h5': { fontSize: '1rem' },
                    '& h6': { fontSize: '0.875rem' },

                    // Paragraphs
                    '& p': {
                        marginBottom: '1em',
                        '&:last-child': {
                            marginBottom: 0,
                        },
                    },

                    // Lists
                    '& ul, & ol': {
                        marginBottom: '1em',
                        paddingLeft: '1.5em',
                    },
                    '& li': {
                        marginBottom: '0.25em',
                        '& p': {
                            marginBottom: '0.5em',
                        },
                    },

                    // Code blocks
                    '& pre': {
                        backgroundColor: isDarkMode ? 'rgba(45, 45, 45, 0.8)' : 'rgba(247, 247, 248, 0.8)',
                        border: `1px solid ${isDarkMode ? 'rgba(86, 88, 105, 0.5)' : 'rgba(217, 217, 227, 0.8)'}`,
                        borderRadius: '8px',
                        padding: '1rem',
                        marginBottom: '1em',
                        overflow: 'auto',
                        fontSize: '0.875rem',
                        lineHeight: 1.5,

                        '& code': {
                            backgroundColor: 'transparent',
                            padding: 0,
                            border: 'none',
                            borderRadius: 0,
                            fontSize: 'inherit',
                            color: 'inherit',
                        },
                    },

                    // Inline code
                    '& code': {
                        backgroundColor: isDarkMode ? 'rgba(64, 65, 79, 0.6)' : 'rgba(229, 231, 235, 0.8)',
                        color: isDarkMode ? '#e5e7eb' : '#374151',
                        padding: '0.125rem 0.375rem',
                        borderRadius: '4px',
                        fontSize: '0.875rem',
                        fontFamily: 'Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                        border: `1px solid ${isDarkMode ? 'rgba(86, 88, 105, 0.3)' : 'rgba(209, 213, 219, 0.5)'}`,
                    },

                    // Blockquotes
                    '& blockquote': {
                        borderLeft: `4px solid ${isDarkMode ? '#4f46e5' : '#6366f1'}`,
                        paddingLeft: '1rem',
                        marginLeft: 0,
                        marginBottom: '1em',
                        fontStyle: 'italic',
                        color: isDarkMode ? '#c5c5d2' : '#6b7280',
                        backgroundColor: isDarkMode ? 'rgba(64, 65, 79, 0.3)' : 'rgba(247, 247, 248, 0.5)',
                        padding: '0.75rem 1rem',
                        borderRadius: '0 8px 8px 0',
                    },

                    // Tables
                    '& table': {
                        width: '100%',
                        borderCollapse: 'collapse',
                        marginBottom: '1em',
                        border: `1px solid ${isDarkMode ? 'rgba(86, 88, 105, 0.5)' : 'rgba(217, 217, 227, 0.8)'}`,
                        borderRadius: '8px',
                        overflow: 'hidden',
                    },
                    '& th, & td': {
                        padding: '0.75rem',
                        textAlign: 'left',
                        borderBottom: `1px solid ${isDarkMode ? 'rgba(86, 88, 105, 0.3)' : 'rgba(217, 217, 227, 0.5)'}`,
                    },
                    '& th': {
                        backgroundColor: isDarkMode ? 'rgba(64, 65, 79, 0.5)' : 'rgba(247, 247, 248, 0.8)',
                        fontWeight: 600,
                        color: isDarkMode ? '#ececf1' : '#374151',
                    },
                    '& tr:last-child td': {
                        borderBottom: 'none',
                    },

                    // Links
                    '& a': {
                        color: isDarkMode ? '#FF8F5A' : '#FE6A2E',
                        textDecoration: 'none',
                        '&:hover': {
                            textDecoration: 'underline',
                        },
                    },

                    // Horizontal rules
                    '& hr': {
                        border: 'none',
                        borderTop: `1px solid ${isDarkMode ? 'rgba(86, 88, 105, 0.5)' : 'rgba(217, 217, 227, 0.8)'}`,
                        margin: '1.5em 0',
                    },

                    // Strong/Bold text
                    '& strong, & b': {
                        fontWeight: 600,
                        color: isDarkMode ? '#ececf1' : '#1f2937',
                    },

                    // Emphasis/Italic text
                    '& em, & i': {
                        fontStyle: 'italic',
                    },

                    // Task lists (GitHub Flavored Markdown)
                    '& .task-list-item': {
                        listStyle: 'none',
                        marginLeft: '-1.5em',

                        '& input[type="checkbox"]': {
                            marginRight: '0.5em',
                        },
                    },
                },
                ...sx,
            }}
        >
            <div className="markdown-content">
                <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[rehypeHighlight]}
                    components={{
                        // Custom component for handling task lists
                        li: ({ children, className, ...props }) => {
                            const isTaskList = className?.includes('task-list-item');
                            return (
                                <li
                                    className={isTaskList ? 'task-list-item' : ''}
                                    {...props}
                                >
                                    {children}
                                </li>
                            );
                        },
                    }}
                >
                    {content}
                </ReactMarkdown>
            </div>
        </Box>
    );
};

export default MarkdownRenderer; 